import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { useMemo } from 'react';

import { useBevoegdGezag } from '@/api/queries/bevoegdGezag';
import { Button } from '@/components/Button/Button';
import { DocumentTitle } from '@/components/DocumentTitle';
import { DropdownMenu } from '@/components/DropdownMenu/DropdownMenu';
import { Explorer } from '@/components/Explorer';
import { ModalTrigger } from '@/components/Modal';
import { GRONDSLAGEN } from '@/constants/grondslagen';
import { eStrings } from '@/constants/strings';
import { BevoegdGezagAdd } from '@/modals/BevoegdGezagAdd';
import { BevoegdGezagDeactivate } from '@/modals/BevoegdGezagDeactivate';
import { BevoegdGezagEdit } from '@/modals/BevoegdGezagEdit';
import { BevoegdGezagHistorie } from '@/modals/BevoegdGezagHistorie';
import { BevoegdGezagReactivate } from '@/modals/BevoegdGezagReactivate';
import { useUserStore } from '@/stores/user';
import { useOffsetTop } from '@/utils/hooks/useOffsetTop';

import { SidebarLayout } from './-components/SidebarLayout';

const redirectLandelijk = () => {
  if (useUserStore.getState().area !== 'landelijk') return;

  throw redirect({
    to: '/beheer/standaardteksten/$grondslagId',
    params: { grondslagId: GRONDSLAGEN[0].id },
    search: { p: 1 },
  });
};

/**
 * Query options
 */
const bevoegdGezagQueryOptions = () =>
  useBevoegdGezag.getOptions({
    page: 1,
    size: 1000,
    filter: [
      {
        field: 'Landelijk',
        op: 'eq',
        value: '0',
      },
      {
        field: 'Volgend',
        op: 'is_null',
        value: '',
      },
      {
        model: 'BevoegdGezagLineage',
        field: 'organization',
        op: 'eq',
        value: '',
      },
    ],
    sort: [{ field: 'Naam', direction: 'asc' }],
  });

export const Route = createFileRoute('/beheer/bevoegdegezagen')({
  beforeLoad: () => {
    redirectLandelijk();

    return {
      getTitle: () => 'Bevoegde gezagen',
    };
  },
  loader: async ({ context: { queryClient } }) => {
    await queryClient.ensureQueryData(bevoegdGezagQueryOptions());
  },
  component: BevoegdeGezagenComponent,
});

function BevoegdeGezagenComponent() {
  const bevoegdeGezagen = useQuery(bevoegdGezagQueryOptions());
  const objects = useMemo(
    () => bevoegdeGezagen.data?.objects ?? [],
    [bevoegdeGezagen.data]
  );

  const { ref: listRef, offsetTop } = useOffsetTop();

  const virtualizer = useWindowVirtualizer({
    count: objects.length,
    estimateSize: () => 60,
    overscan: 10,
    enabled: !!objects.length,
    scrollMargin: offsetTop,
    paddingEnd: 300,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();

  return (
    <SidebarLayout>
      <DocumentTitle
        sections={[eStrings.LBL_DIGI_V, 'Beheer Bevoegde gezagen']}
      />
      <Explorer
        title="Bevoegde gezagen"
        button={
          <ModalTrigger
            modalComponent={BevoegdGezagAdd}
            onOpen={() => ({})}
            asChild
          >
            <Button variant="secondary">Nieuw bevoegd gezag</Button>
          </ModalTrigger>
        }
      >
        {objects.length ? (
          <div
            ref={listRef}
            className="relative w-full"
            style={{ height: totalSize }}
          >
            <ul
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${(virtualItems[0]?.start ?? 0) - offsetTop}px)`,
              }}
            >
              {virtualItems.map((item) => {
                const bevoegdGezag = objects[item.index];

                if (!bevoegdGezag) return null;

                return (
                  <li
                    key={bevoegdGezag.ID}
                    ref={virtualizer.measureElement}
                    data-index={item.index}
                  >
                    <Explorer.Button
                      asChild
                      icon="landmark"
                      dropdownMenuContent={
                        <>
                          <ModalTrigger
                            modalComponent={BevoegdGezagEdit}
                            onOpen={() => ({ bevoegdGezag })}
                            asChild
                          >
                            <DropdownMenu.Button>Bewerken</DropdownMenu.Button>
                          </ModalTrigger>
                          {bevoegdGezag.Stop_Lineage ? (
                            <ModalTrigger
                              modalComponent={BevoegdGezagReactivate}
                              onOpen={() => ({ bevoegdGezag })}
                              asChild
                            >
                              <DropdownMenu.Button>
                                Heractiveren
                              </DropdownMenu.Button>
                            </ModalTrigger>
                          ) : (
                            <ModalTrigger
                              modalComponent={BevoegdGezagDeactivate}
                              onOpen={() => ({ bevoegdGezag })}
                              asChild
                            >
                              <DropdownMenu.Button>
                                Deactiveren
                              </DropdownMenu.Button>
                            </ModalTrigger>
                          )}
                          {bevoegdGezag.Vorig && (
                            <ModalTrigger
                              modalComponent={BevoegdGezagHistorie}
                              onOpen={() => ({ bevoegdGezag })}
                              asChild
                            >
                              <DropdownMenu.Button>
                                Historie
                              </DropdownMenu.Button>
                            </ModalTrigger>
                          )}
                        </>
                      }
                      inactive={!!bevoegdGezag.Stop_Lineage}
                    >
                      <div>
                        {bevoegdGezag.Naam}{' '}
                        {!!bevoegdGezag.Stop_Lineage &&
                          '(onzichtbaar voor gebruikers)'}
                      </div>
                    </Explorer.Button>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : (
          <Explorer.Placeholder>
            Er zijn nog geen bevoegde gezagen.
          </Explorer.Placeholder>
        )}
      </Explorer>
    </SidebarLayout>
  );
}
